import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, ArrowLeftRight, FileUp, BarChart3, CalendarDays, CalendarRange, Repeat, Settings, LogOut, History, BookOpen, Menu, X, CalendarClock, Users, Calendar, HardHat, Scale, ReceiptText, Megaphone, Car, ArrowUp, HandCoins, ExternalLink, ChevronDown, Building2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { ReleaseNotes } from "./ReleaseNotes";
import api from "../lib/api";

const navGroups = [
  {
    label: "Novac",
    items: [
      { to: "/", label: "Pregled", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/transakcije", label: "Transakcije", icon: ArrowLeftRight, testid: "nav-transactions" },
      { to: "/dnevni-saldo", label: "Dnevni saldo", icon: CalendarDays, testid: "nav-daily-balance" },
      { to: "/kalendar", label: "Kalendar", icon: Calendar, testid: "nav-calendar" },
    ],
  },
  {
    label: "Unos",
    items: [
      { to: "/uvoz", label: "Uvoz izvoda", icon: FileUp, testid: "nav-import" },
      { to: "/racuni", label: "Računi", icon: ReceiptText, testid: "nav-invoices" },
      { to: "/nenaplaceno", label: "Nenaplaćeno", icon: HandCoins, testid: "nav-receivables" },
      { to: "/fiksni-troskovi", label: "Fiksni troškovi/prihodi", icon: Repeat, testid: "nav-recurring" },
      { to: "/uskladivanje", label: "Usklađivanje", icon: Scale, testid: "nav-reconciliation" },
    ],
  },
  {
    label: "Analiza",
    items: [
      { to: "/izvjestaji", label: "Izvještaji", icon: BarChart3, testid: "nav-reports" },
      { to: "/godisnji", label: "Godišnji pregled", icon: CalendarRange, testid: "nav-annual" },
      { to: "/partneri", label: "Partneri", icon: Users, testid: "nav-partners" },
      { to: "/radnici", label: "Radnici", icon: HardHat, testid: "nav-workers" },
      { to: "/vozila", label: "Vozila", icon: Car, testid: "nav-vehicles" },
    ],
  },
  {
    label: "Sustav",
    items: [
      { to: "/aktivnost", label: "Aktivnost", icon: History, testid: "nav-activity" },
      { to: "/novosti", label: "Novosti", icon: Megaphone, testid: "nav-news" },
      { to: "/upute", label: "Upute", icon: BookOpen, testid: "nav-help" },
      { to: "/postavke", label: "Postavke", icon: Settings, testid: "nav-settings" },
      { to: "/administracija", label: "Administracija", icon: ShieldCheck, testid: "nav-admin" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { companies, active, company, switchCompany } = useCompany();
  const [open, setOpen] = useState(false);
  const [overdue, setOverdue] = useState(null);
  const [showTop, setShowTop] = useState(false);
  const location = useLocation();
  const vehiclesEnabled = !company || company.features?.vehicles !== false;
  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    api.get("/overdue").then((r) => setOverdue(r.data)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 bg-[#111827] z-40 flex items-center justify-between px-4">
        <img src="/cashline-logo-white.png" alt="Cashline" className="h-7 w-auto" />
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="text-white p-2 -mr-2 relative"
          aria-label="Izbornik"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          {!open && overdue?.count > 0 && (
            <span data-testid="mobile-overdue-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#EF4444] rounded-full text-[10px] font-bold text-white flex items-center justify-center leading-none">
              {overdue.count}
            </span>
          )}
        </button>
      </header>

      {open && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} />}

      <aside className={`fixed inset-y-0 left-0 w-60 bg-[#111827] flex flex-col z-50 transform transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="px-6 py-5 border-b border-white/15">
          <img src="/cashline-logo-white.png" alt="Cashline" className="h-9 w-auto" data-testid="sidebar-logo" />
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#9CA3AF] mt-2.5">Cash Flow Management</p>
          {companies.length > 1 ? (
            <div className="mt-3 relative">
              <select
                data-testid="company-switcher"
                value={active}
                onChange={(e) => switchCompany(e.target.value)}
                className={`w-full rounded-sm text-white text-sm font-semibold pl-2.5 pr-8 py-1.5 appearance-none cursor-pointer border transition-colors duration-200 ${
                  active === "masterfb" ? "bg-[#C48A31]/25 border-[#C48A31]/60 hover:bg-[#C48A31]/35" : "bg-white/10 border-white/25 hover:bg-white/15"
                }`}
              >
                {companies.map((c) => (
                  <option key={c.id} value={c.id} className="text-[#0F172A]">{c.name}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
            </div>
          ) : company ? (
            <p
              data-testid="company-label"
              className={`mt-3 flex items-center gap-2 text-sm font-semibold text-white rounded-sm px-2.5 py-1.5 border ${
                active === "masterfb" ? "bg-[#C48A31]/25 border-[#C48A31]/60" : "bg-white/10 border-white/25"
              }`}
            >
              <Building2 className="w-3.5 h-3.5 shrink-0 opacity-80" /> {company.name}
            </p>
          ) : null}
        </div>
        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          {navGroups.map((g, gi) => (
            <div key={g.label} className={gi > 0 ? "mt-3" : ""}>
              <p className="px-3 pb-1 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6B7280]">{g.label}</p>
              <div className="space-y-0.5">
                {g.items.filter(({ to }) => (to !== "/vozila" || vehiclesEnabled) && (to !== "/administracija" || isAdmin)).map(({ to, label, icon: Icon, testid }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    data-testid={testid}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors duration-200 ${
                        isActive ? "bg-white/20 text-white" : "text-[#9CA3AF] hover:bg-white/10 hover:text-white"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{label}</span>
                    {to === "/transakcije" && overdue?.count > 0 && (
                      <span data-testid="nav-overdue-badge" className="ml-auto bg-[#EF4444] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                        {overdue.count}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-white/15">
          <a
            href="https://vtz-hr.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="vtz-hr-link"
            title="Isti Google račun radi u obje aplikacije"
            className="flex items-center gap-3 px-3 py-2 mb-2 rounded-sm text-sm font-semibold bg-[#2F7D4F] text-white hover:bg-[#27693F] transition-colors duration-200"
          >
            <HardHat className="w-4 h-4 shrink-0" />
            VTZ HR
            <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-70" />
          </a>
          <p className="px-3 text-xs text-[#9CA3AF] truncate mb-1.5" data-testid="user-email">{user?.name || user?.email}</p>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="flex w-full items-center gap-3 px-3 py-2 rounded-sm text-sm text-[#9CA3AF] hover:bg-white/10 hover:text-white transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" />
            Odjava
          </button>
        </div>
      </aside>

      <main className="lg:ml-60 p-4 pt-[4.5rem] md:p-6 md:pt-[4.5rem] lg:p-8 lg:pt-6">
        <ReleaseNotes />
        {overdue?.count > 0 && (
          <Link
            to="/transakcije"
            data-testid="global-overdue-banner"
            className="flex items-center gap-2.5 bg-[#DC2626] text-white rounded-sm px-4 py-2 mb-4 text-sm shadow-sm hover:bg-[#8F4237] transition-colors duration-200"
          >
            <CalendarClock className="w-4 h-4 shrink-0" />
            <p className="truncate min-w-0">
              <span className="font-bold">Dospjelo:</span> {overdue.count} {overdue.count === 1 ? "plan" : "planova"} nije ostvareno ({overdue.total_eur.toLocaleString("hr-HR", { minimumFractionDigits: 2 })} €)<span className="hidden sm:inline"> — kliknite za pregled</span>
            </p>
          </Link>
        )}
        <Outlet />
      </main>

      {showTop && (
        <button
          data-testid="scroll-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Natrag na vrh"
          className="fixed bottom-5 right-5 z-40 w-10 h-10 rounded-full bg-[#0D9488] text-white shadow-lg flex items-center justify-center hover:bg-[#0F766E] transition-colors duration-200"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
